import { ComponentType, useMemo } from "react";

import { Box, SvgIcon, SxProps } from "@mui/material";
import { SvgIconProps } from "@mui/material/SvgIcon";
import { StriimTypography } from "@striim/striim-ui-v2";

import {
    BlocksCategory,
    GitPullRequestCategory,
    MessageCategory,
    SearchCategory,
    SourceIconCategory,
    TargetIconCategory,
    TransformCategory,
    AiAgentCategory,
    ExternalSourceCategory
} from "../../../../../../src/generic/icon/customIcons";

import { FilterBoolean, Styles, StylesTheme } from "../../../../../../src/utils/types";

interface CategoryButtonProps {
    onClick: () => void;
    label: string;
    active: boolean;
}

const CategoryButton = ({ onClick, label, active }: CategoryButtonProps) => {
    const rootSX: SxProps = useMemo(() => [styles.root, active && styles.active].filter(FilterBoolean), [active]);

    return (
        <Box
            sx={rootSX}
            onClick={onClick}
            data-testid={label}
            name={`category-button-${active ? "active" : "inactive"}`}
        >
            <Box sx={styles.icon}>
                <SvgIcon component={Icons[label] || Icons["Base"]} />
            </Box>
            <StriimTypography sx={styles.label}>{label}</StriimTypography>
        </Box>
    );
};

export default CategoryButton;

const Icons: {
    [key: string]: ComponentType<SvgIconProps>;
} = {
    // Use stroke="currentColor" everywhere
    Base: (BlocksCategory as unknown) as ComponentType<SvgIconProps>,
    Enrich: (GitPullRequestCategory as unknown) as ComponentType<SvgIconProps>,
    Comments: (MessageCategory as unknown) as ComponentType<SvgIconProps>,
    Search: (SearchCategory as unknown) as ComponentType<SvgIconProps>,
    Source: (SourceIconCategory as unknown) as ComponentType<SvgIconProps>,
    Target: (TargetIconCategory as unknown) as ComponentType<SvgIconProps>,
    Transform: (TransformCategory as unknown) as ComponentType<SvgIconProps>,
    "AI Agents": (AiAgentCategory as unknown) as ComponentType<SvgIconProps>,
    "External Sources": (ExternalSourceCategory as unknown) as ComponentType<SvgIconProps>
};

const styles: Styles = {
    root: {
        display: "flex",
        width: "52px",
        padding: "4px",
        flexDirection: "column",
        alignItems: "center",
        gap: "2px",

        userSelect: "none",

        borderRadius: "4px",
        cursor: "pointer",
        transition: "all 0.2s ease",

        color: ({ palette }: StylesTheme) => palette.greyscale[700],

        "&:hover,&:focus": {
            backgroundColor: ({ palette }: StylesTheme) => palette.greyscale[50]
        },
        "&:hover": {
            color: ({ palette }: StylesTheme) => palette.greyscale[900],
            transform: "scale(1.1)"
        },
        "&:focus": {
            color: ({ palette }: StylesTheme) => palette.primary[800]
        },
        "&:active": {
            backgroundColor: ({ palette }: StylesTheme) => palette.greyscale[300],
            color: ({ palette }: StylesTheme) => palette.primary[800],
            transform: "scale(1.1)"
        },

        "& svg": {
            fill: "none",
            color: "inherit"
        }
    },
    active: {
        cursor: "default",
        backgroundColor: ({ palette }: StylesTheme) => palette.greyscale[200],
        color: ({ palette }: StylesTheme) => palette.primary[800],

        "&:hover, &:focus, &:active": {
            backgroundColor: ({ palette }: StylesTheme) => palette.greyscale[200],
            color: ({ palette }: StylesTheme) => palette.primary[800]
        }
    },
    label: {
        textAlign: "center",
        color: "inherit",
        fontFeatureSettings: "'liga' off, 'clig' off",
        fontFamily: "Inter",
        fontSize: "10px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "12px",
        letterSpacing: "0.2px"
    },
    icon: {
        width: "22px",
        height: "22px"
    }
};
